import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useAuthContext from '../../hooks/useAuthContext';
import useInviteLink from '../../hooks/useInviteLink';
import { LOGIN } from '../../routes/app/paths';
import girlsImg from '../../assets/images/girls.webp';
import Modal from '../Layout/Modal';
import InviteFriend from '../Common/InviteFriend';

const steps = (t) => [
  {
    badge: t('referral.step1Badge'),
    title: t('referral.step1Title'),
    description: t('referral.step1Description'),
  },
  {
    badge: t('referral.step2Badge'),
    title: t('referral.step2Title'),
    description: t('referral.step2Description'),
  },
  {
    badge: t('referral.step3Badge'),
    title: t('referral.step3Title'),
    description: t('referral.step3Description'),
  },
];

export default function ReferralSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthContext();
  const { code: invitationCode, isModalOpen, copyInviteLink, closeModal } = useInviteLink();

  const handleCTA = () => {
    if (!isAuthenticated) {
      navigate(LOGIN);
      return;
    }
    copyInviteLink();
  };

  return (
    <section className="relative overflow-hidden bg-white py-16 lg:py-24">
      <div className="flex gap-12 justify-between items-center">
        {/* Left: content */}
        <div className="flex-1 flex mx-4 md:mx-40 z-10">
          <div className="flex flex-col gap-8">
            {/* Heading */}
            <div>
              <h2 className="text-3xl lg:text-4xl font-brave-hunter text-[#187672] leading-tight">
                {t('referral.title')}
              </h2>
              <p className="mt-3 text-gray-600 text-base lg:text-lg">
                {t('referral.subtitle')}{' '}
                <span className="text-[#D9A520] font-semibold">
                  {t('referral.subtitleHighlight')}
                </span>
              </p>
            </div>

            {/* Steps */}
            <div className="flex flex-col gap-4 max-w-md">
              {steps(t).map((step) => (
                <div
                  key={step.badge}
                  className="bg-gray-100/90 rounded-2xl shadow-sm border border-gray-200 p-5"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="inline-block bg-gray-200 text-[#187672] text-xs font-semibold px-3 py-1 rounded-full">
                      {step.badge}
                    </span>
                    <h3 className="text-lg lg:text-xl font-bold text-gray-800">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div>
              <button
                onClick={handleCTA}
                className="bg-[#187672] hover:bg-[#136361] transition-colors text-white font-medium text-sm lg:text-base px-10 py-4 rounded-full"
              >
                {isAuthenticated
                  ? t('referral.ctaAuthenticated')
                  : t('referral.ctaGuest')}
              </button>
            </div>
          </div>
        </div>

        {/* Right: image with decorative circles */}
        <div className="absolute bottom-0 right-0">
          <div className="relative flex items-center justify-center h-[520px] w-[600px] overflow-hidden origin-bottom-right scale-125">
            {/* Yellow/lime circle (overlapping center-bottom) */}
            <div className="absolute -bottom-20 left-0 w-[470px] h-[470px] rounded-full bg-[#D9E022] z-0" />

            {/* Gray circle (top-left background) */}
            <div className="absolute top-1 left-1/5 w-[370px] h-[370px] rounded-full bg-gray-100 z-0" />

            {/* Green circle (main large circle) */}
            <div className="absolute -right-1/4 bottom-10 w-[420px] h-[420px] rounded-full bg-[#187672] z-0" />

            {/* Girls image */}
            <img
              src={girlsImg}
              alt="Two friends sharing Tropiphone"
              className="absolute right-0 bottom-0 z-10 w-full max-w-sm lg:max-w-md object-contain drop-shadow-lg"
            />
          </div>
        </div>
      </div>

      <Modal show={isModalOpen} onClose={closeModal}>
        <InviteFriend code={invitationCode} />
      </Modal>
    </section>
  );
}
